<script setup>
import SiteLogo from 'components/SiteLogo.vue';
import UserMenuToggle from 'components/UserMenuToggle.vue';
import { useBreakpoint } from 'composables/breakpoint';
import { useQuasar } from 'quasar';
import AppRoute from 'src/router/app-route';
import { Route } from 'src/router/routes';
import { useAppStore } from 'stores/app-store';
import { useAuthStore } from 'stores/auth-store';
import { useSiteContextStore } from 'stores/site-context-store';
import { computed, onMounted, onUnmounted, ref } from 'vue';

const $q = useQuasar();
const appStore = useAppStore();
const authStore = useAuthStore();
const breakpoint = useBreakpoint();
const siteContextStore = useSiteContextStore();

const isScrolled = ref(false);

const jobSeekerNavLinks = [
    { text: 'Home', route: AppRoute.build(Route.LandingPage) },
    { text: 'Job Search', route: AppRoute.build(Route.SearchResultsPage) },
    { text: 'My Profile', route: AppRoute.build(Route.ProfileManagerPage) }
];

const employerNavLinks = [
    { text: 'Home', route: AppRoute.build(Route.EmployerLandingPage) },
    { text: 'Pricing', route: AppRoute.build(Route.PricingPage) },
    { text: 'My Jobs', route: AppRoute.build(Route.JobManagerListPage) }
];

const activeNavLinks = computed(() => {
    return siteContextStore.isEmployerSiteContext
        ? employerNavLinks
        : jobSeekerNavLinks;
});

const currentSiteSwitcherLabel = computed(() => {
    return siteContextStore.isEmployerSiteContext
        ? 'Go to Job Seekers Site'
        : 'Go to Employers Site';
});

const currentSiteSwitcherRoute = computed(() => {
    return siteContextStore.isEmployerSiteContext
        ? AppRoute.build(Route.LandingPage)
        : AppRoute.build(Route.EmployerLandingPage);
});

const logoWidth = computed(() => {
    return !breakpoint.lt.md
        ? '243px'
        : '165px';
});

const logoHeight = computed(() => {
    return !breakpoint.lt.md
        ? '40px'
        : '27px';
});

onMounted(() => {
    window.addEventListener('scroll', updateIsScrolledValue);
    updateIsScrolledValue();
});

onUnmounted(() => {
    window.removeEventListener('scroll', updateIsScrolledValue);
});

function updateIsScrolledValue() {
    isScrolled.value = window.scrollY > 0;
}
</script>

<template>
    <q-header :class="['flex', { 'shadow-3': isScrolled }]" :height-hint="70" :reveal-offset="150" reveal @reveal="appStore.setHeaderRevealed">
        <q-toolbar class="container-md q-px-md">
            <div class="flex row full-width">
                <div class="logo-section flex row items-center col-auto">
                    <SiteLogo :height="logoHeight" :inverted="$q.dark.isActive" :width="logoWidth"/>
                </div>
                <q-space/>
                <nav v-if="!breakpoint.lt.md" class="nav-section flex row items-center">
                    <router-link v-for="link in activeNavLinks" :key="link.text" :to="link.route" class="nav-link">
                        {{ link.text }}
                    </router-link>
                </nav>
                <q-space/>
                <div class="right-section flex row q-gutter-x-md q-ml-auto q-mr-none items-center">
                    <q-btn :label="currentSiteSwitcherLabel" :to="currentSiteSwitcherRoute" class="site-switcher hidden inline-flex-sm" color="primary" flat no-caps/>
                    <UserMenuToggle class="cursor-pointer" title="Toggle user menu"/>
                    <q-btn v-if="!authStore.isUserAuthenticated" :to="AppRoute.build(Route.LoginPage)" aria-label="Log in" color="primary" flat icon="sym_r_login" padding="6px"/>
                </div>
            </div>
        </q-toolbar>
    </q-header>
</template>

<style lang="scss" scoped>
.q-header {
    backdrop-filter: blur(5px);
    background-color: rgba($white, 0.9);
    height: 70px;

    .body--dark & {
        background: rgba($dark, 0.9);
    }
}

.logo-section {
    height: auto;
}

.q-toolbar,
.q-toolbar > div {
    flex: 1;
}

.nav-section {
    .nav-link {
        border-radius: 10px;
        margin: 0 6px;
        padding: 8px 16px;
        position: relative;
        text-decoration: none;
        transition: all 0.25s cubic-bezier(0.25, 1, 0.5, 1);

        &::after {
            background: var(--q-primary);
            border-radius: 3px;
            bottom: -2px;
            content: '';
            height: 3px;
            left: 50%;
            opacity: 0;
            position: absolute;
            transform: translateX(-50%);
            transition: width 120ms ease, opacity 0.3s ease;
            width: 0;
        }

        &:hover {
            color: var(--q-primary);

            &::after {
                opacity: 0.5;
                width: 40%;
            }
        }

        &.router-link-active {
            background: rgba(var(--q-primary), 0.08);
            color: var(--q-primary);
            font-weight: 600;

            &::after {
                height: 3px;
                opacity: 1;
                width: 70%;
            }
        }

        .body--dark & {
            color: rgba(255, 255, 255, 0.75);

            &:hover {
                background: rgba(255, 255, 255, 0.1);
                color: white;
            }

            &.router-link-active {
                background: rgba(var(--q-primary), 0.25);
                color: white;

                &::after {
                    background: var(--q-primary);
                }
            }
        }
    }
}
</style>
